import { useEffect, useState } from 'react';
import { api } from './api';

// Motion is a 5-min rollup (motion_activity), never an event feed.
export const getMovementLive = () => api.get('/movement/live').then((r) => r.data || {});
export const getRoomOccupancy = () => api.get('/rooms/occupancy').then((r) => r.data || {});
export const getPatientPresence = (id) => api.get(`/patients/${id}/presence`).then((r) => r.data || {});
export const getMovementHistory = (params) =>
  api.get('/movement/history', { params: { group_by: 'device', ...params } })
    .then((r) => (Array.isArray(r.data?.buckets) ? r.data.buckets : []));

export const RANGES = {
  '24h': { params: { hours: 24, resolution: '5m' }, stepMin: 5, spanMin: 24 * 60 },
  '7d': { params: { days: 7, resolution: 'hour' }, stepMin: 60, spanMin: 7 * 24 * 60 },
  '30d': { params: { days: 30, resolution: 'hour' }, stepMin: 60, spanMin: 30 * 24 * 60 },
};

// tone drives colour; fault states are visually distinct from stillness.
export const STATE_META = {
  moving: { tone: 'ok', key: 'mv_state_moving' },
  occupied: { tone: 'ok', key: 'mv_state_moving' },
  still: { tone: 'calm', key: 'mv_state_still' },
  vacant: { tone: 'calm', key: 'mv_state_still' },
  inactive: { tone: 'warn', key: 'mv_state_inactive' },
  silent: { tone: 'fault', key: 'mv_state_silent' },
  never_reported: { tone: 'muted', key: 'mv_state_never' },
  no_motion_data: { tone: 'muted', key: 'mv_state_nodata' },
  unknown: { tone: 'muted', key: 'mv_state_nodata' },
  no_motion_sensors: { tone: 'muted', key: 'mv_state_no_sensor' },
};

export const latestIso = (list) =>
  list.filter(Boolean).sort((a, b) => new Date(b) - new Date(a))[0] || null;

// Worst-first so one silent sensor is never hidden behind a moving one.
const PRIORITY = ['silent', 'inactive', 'moving', 'still', 'no_motion_data', 'never_reported'];
export function combinedState(devices) {
  const states = devices.map((d) => d.state);
  return PRIORITY.find((s) => states.includes(s)) || states[0] || 'no_motion_data';
}

// Expand sparse buckets into a full timeline; a missing slot = not reporting.
export function buildTimeline(buckets, deviceIds, rangeKey, nowMs = Date.now()) {
  const { stepMin, spanMin } = RANGES[rangeKey];
  const step = stepMin * 60000;
  const ids = new Set(deviceIds);
  const bySlot = new Map();
  let first = null;
  for (const b of buckets) {
    if (!ids.has(b.group_key)) continue;
    const ts = Math.floor(new Date(b.bucket_start).getTime() / step) * step;
    const cur = bySlot.get(ts) || { samples: 0, motion: 0 };
    cur.samples += b.samples || 0;
    cur.motion += b.motion_samples || 0;
    bySlot.set(ts, cur);
    if (first == null || ts < first) first = ts;
  }
  if (first == null) return { points: [], firstMs: null };
  const end = Math.floor(nowMs / step) * step;
  const start = Math.max(first, end - spanMin * 60000 + step);
  const points = [];
  for (let ts = start; ts <= end; ts += step) {
    const v = bySlot.get(ts);
    points.push({ ts, stepMin, motion: v ? v.motion : 0, samples: v ? v.samples : 0, gap: !v });
  }
  return { points, firstMs: first };
}

// Live poll every 30s: last_seen / last_motion_at move faster than the 5-min bucket.
export function useMovementLive() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => getMovementLive().then((d) => alive && setLive(d)).catch(() => alive && setLive({ devices: [] }));
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return live;
}

// Attribution rule: a sensor is a resident's own movement only if it is assigned
// to them, or sits in their room and they are the room's only resident.
export function residentSensorIds(p, sensors, roomResidentCount) {
  const own = sensors.filter((s) => s.resident_uuid === p.id).map((s) => s.device_uuid);
  const room = sensors.filter((s) => !s.resident_uuid && p.room_id && s.assigned_room_id === p.room_id).map((s) => s.device_uuid);
  const shared = roomResidentCount > 1 && room.length > 0;
  return { ids: shared ? own : [...own, ...room], shared, roomIds: room };
}

// Presence devices ('link' = assigned | room) filtered with the same rule.
export function ownPresenceDevices(presence, roomResidentCount) {
  const devs = presence?.devices || [];
  const shared = presence?.shared_room ?? (roomResidentCount > 1);
  const room = devs.filter((d) => d.link === 'room');
  return { devices: shared ? devs.filter((d) => d.link !== 'room') : devs, shared: presence?.shared_room === true || (shared && room.length > 0) };
}

// On error: family fails closed (treat as shared), staff fails open (keep the chart).
export const getRoomResidentCount = (roomId, { failClosed = false } = {}) => (roomId
  ? api.get(`/rooms/${roomId}/patients`).then((r) => (Array.isArray(r.data) ? r.data : (r.data?.items || [])).filter((p) => !p.deleted_at).length).catch(() => (failClosed ? 2 : 1))
  : Promise.resolve(0));
